import { motion } from "framer-motion";
import { Compass, Target, Zap } from "lucide-react";
import CTASection from "@/components/CTASection";
import SectionLabel from "@/components/SectionLabel";
import BrandMarquee from "@/components/BrandMarquee";
import ParallaxImage from "@/components/ParallaxImage";
import { IMAGES } from "@/data/content";

const VALUES = [
  { Icon: Compass, t: "Strategy-first", d: "Every campaign starts with audience alignment and outcome clarity — not just creator outreach." },
  { Icon: Target, t: "Verified & relevant", d: "We onboard creators with real engagement, niche fit and authentic audiences." },
  { Icon: Zap, t: "Built for scale", d: "Structured workflows let brands run high-volume campaigns without operational chaos." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#050505] text-[#F3E8E0]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden grain-bg">
        <div className="absolute top-32 left-1/4 w-[600px] h-[600px] bg-[#FF4A00] opacity-[0.08] rounded-full blur-[180px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl">
            <SectionLabel className="mb-6">About Incer</SectionLabel>
            <h1
              data-testid="about-hero-title"
              className="font-display text-4xl md:text-6xl lg:text-[7rem] font-semibold tracking-tighter leading-[0.92]"
            >
              Making influencer marketing{" "}
              <span className="italic text-[#FF4A00] font-medium">smarter, scalable, strategic.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 border-t border-[#1a1a1a]" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionLabel className="mb-5">Our story</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter leading-[0.95]">
                Bridging strategy, creators and measurable outcomes.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-lg text-[#A3A3A3] leading-relaxed">
              <p>
                Incer is a strategy-driven influencer marketing platform built to help brands and agencies
                discover the right creators, manage influencer campaigns efficiently, and measure real
                performance through data-driven insights.
              </p>
              <p>
                As influencer marketing continues to transform the way brands connect with audiences, the
                need for a structured and reliable system has never been greater. Incer bridges this gap by
                combining influencer discovery, campaign management and real-time analytics into one
                powerful platform.
              </p>
              <p className="text-[#F3E8E0] font-medium text-xl">
                Our mission is simple: to make influencer marketing smarter, scalable and strategic.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE BLOCK */}
      <section className="py-12 md:py-16" data-testid="about-image">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ParallaxImage
              src={IMAGES.about1}
              alt="Team collaborating at a whiteboard"
              className="aspect-[4/5] md:aspect-[3/4] rounded-3xl border border-[#1a1a1a]"
              intensity={0.14}
            />
            <ParallaxImage
              src={IMAGES.about2}
              alt="Team meeting in a modern office"
              className="aspect-[4/5] md:aspect-[3/4] rounded-3xl border border-[#1a1a1a] md:mt-16"
              intensity={0.18}
            />
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">What we believe</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              Three principles shape everything we build.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`about-value-${i}`}
                className="card-glow p-10 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
              >
                <div className="font-display text-5xl font-semibold text-[#FF4A00]/30 mb-6">0{i + 1}</div>
                <v.Icon className="w-6 h-6 text-[#FF4A00] mb-5" />
                <h3 className="font-display text-2xl font-medium tracking-tight mb-3">{v.t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT MARQUEE */}
      <section className="py-16 border-t border-[#1a1a1a] bg-[#050505]" data-testid="about-clients">
        <BrandMarquee label="Trusted by ambitious teams" />
      </section>

      <CTASection
        testId="about-cta"
        eyebrow="Work with us"
        title="Let's power your next campaign together."
        description="Whether you're a brand, a creator or an agency — Incer can help you do influence right."
      />
    </div>
  );
}
